import React, { useEffect, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

// Top-level guessing component so it doesn't remount when the parent updates (e.g. timer)
function MostLikelyGuessingSection({ lastResult, gameState, user, submitAction, onGameAction, timeLeft, timeLimit }) {
  const [selectedGuesses, setSelectedGuesses] = useState([]);
  const isWinner = lastResult && lastResult.majorityWinner === (user && user.socketId);
  if (!isWinner) {
    return (
      <div className="mt-4 text-center text-gray-800 dark:text-gray-200">
        Waiting for the winner to guess who voted for them...
      </div>
    );
  }

  const maxGuesses = lastResult && Array.isArray(lastResult.votersForWinner) ? lastResult.votersForWinner.length : 0;
  const serverMax = lastResult && typeof lastResult.maxGuesses === 'number' ? lastResult.maxGuesses : null;
  const effectiveMaxGuesses = (serverMax !== null) ? serverMax : maxGuesses;

  const toggleGuess = (sid) => {
    setSelectedGuesses(prev => {
      if (prev.includes(sid)) return prev.filter(x => x !== sid);
      if (prev.length >= effectiveMaxGuesses) return prev;
      return [...prev, sid];
    });
  };

  const submitGuesses = async () => {
    const action = { type: 'guess', guessed: selectedGuesses };
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  return (
    <div className="mt-4 p-3 border rounded bg-yellow-50 dark:bg-gray-900">
      <div className="font-semibold mb-2 text-gray-800 dark:text-gray-200">You got the most votes — you have up to <strong>{effectiveMaxGuesses}</strong> guess{effectiveMaxGuesses === 1 ? '' : 'es'} to name who voted for you</div>
      <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">Guesses used: <strong>{selectedGuesses.length}</strong> / {effectiveMaxGuesses}</div>
      {/* Guessing timer */}
      <div className="w-full max-w-md mx-auto mt-2 mb-3">
        <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
          <div
            className="h-full bg-yellow-400 transition-none"
            style={{ width: `${(1 - (timeLeft / (timeLimit || 10))) * 100}%`, transition: 'width 0.1s linear' }}
          ></div>
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeft}s left</div>
      </div>
      <div className="grid grid-cols-1 gap-2 mb-3">
        {gameState.players.filter(p => p.socketId !== user.socketId).map(p => {
          const isSelected = selectedGuesses.includes(p.socketId);
          const disableBecauseFull = !isSelected && selectedGuesses.length >= effectiveMaxGuesses;
          return (
            <button key={p.socketId} onClick={() => toggleGuess(p.socketId)} disabled={disableBecauseFull}
              className={`w-full text-left px-3 py-2  text-gray-800 dark:text-gray-200 rounded border ${isSelected ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800'} ${disableBecauseFull ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {p.username || p.socketId} {isSelected ? ' (selected)' : ''}
            </button>
          );
        })}
      </div>
      <div className="text-center">
        <button onClick={submitGuesses} className="px-4 py-2 bg-indigo-600 text-white rounded" disabled={selectedGuesses.length === 0}>Submit Guesses</button>
      </div>
    </div>
  );
}

export default function MostLikelyGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(gameState?.timeLimit || 30);
  const phase = gameState ? gameState.phase : 'waiting';
  const gameRound = gameState && gameState.round ? gameState.round : 0;

  useEffect(() => {
    // clear selection when new round starts
    if (phase === 'voting') setSelected(null);
  }, [phase, gameRound]);

  const startRound = async () => {
    const action = { type: 'startRound' };
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  const vote = async (votedFor) => {
    if (phase !== 'voting') return;
    if (selected) return; // already voted
    setSelected(votedFor);
    const action = { type: 'vote', votedFor };
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  const targetQuestion = gameState && gameState.currentQuestion ? gameState.currentQuestion : null;

  const timeLimit = gameState && gameState.timeLimit ? Number(gameState.timeLimit) : 30;

  // Animated voting timer: uses server-provided startTime and timeLimit when available
  useEffect(() => {
    const limit = timeLimit || 30;
    setTimeLeft(limit);
    // show timer for voting, guessing, and reveal phases
    if (!gameState || !['voting', 'guessing', 'reveal'].includes(gameState.phase)) return undefined;
    const start = gameState.startTime || Date.now();
    let raf = null;
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      const remaining = Math.max(0, Math.ceil(limit - elapsed));
      setTimeLeft(remaining);
      const pct = Math.min(elapsed / limit, 1);
      if (pct < 1 && ['voting', 'guessing', 'reveal'].includes(gameState.phase)) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [gameState, timeLimit]);

  // Compute last round result and awarded shares for display
  const lastResult = (gameState && Array.isArray(gameState.results) && gameState.results.length > 0) ? gameState.results[gameState.results.length - 1] : null;
  const votersForWinner = lastResult && Array.isArray(lastResult.votersForWinner) ? lastResult.votersForWinner : [];
  const majorityWinner = lastResult && lastResult.majorityWinner ? lastResult.majorityWinner : null;
  const awards = lastResult && lastResult.awards ? lastResult.awards : {};
  // awards is a map socketId->points; prepare rounded map
  const awardsRounded = Object.fromEntries(Object.entries(awards).map(([k, v]) => [k, Number((v || 0).toFixed(2))]));

  // ...existing code... (Guessing UI moved to top-level MostLikelyGuessingSection to avoid remounts)

  return (
    <div className="mostlikely-game flex items-center justify-center h-full">
      <div className="w-full max-w-lg p-4 bg-white dark:bg-gray-900 rounded shadow">
        <div className="mb-4 text-center">

          <div className="text-sm text-gray-600 dark:text-gray-300">Round {gameState && gameState.round ? gameState.round : 0} of {gameState && gameState.rounds ? gameState.rounds : '?.'}</div>
        </div>

        {phase === 'waiting' && isHost && (
          <div className="text-center">
            <button onClick={startRound} className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded">Start Round</button>
          </div>
        )}

        {phase === 'voting' && (
          <div>
            <div className="mb-3 text-center text-gray-800 dark:text-gray-200 font-semibold">{targetQuestion}</div>
            {/* Voting timer: progress bar + seconds left */}
            <div className="w-full max-w-md mx-auto mt-2 mb-3">
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                <div
                  className="h-full bg-yellow-400 transition-none"
                  style={{ width: `${(1 - (timeLeft / (timeLimit || 30))) * 100}%`, transition: 'width 0.1s linear' }}
                ></div>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeft}s left</div>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {Array.isArray(gameState && gameState.players ? gameState.players : []) && gameState.players.map((p) => (
                <button key={p.socketId}
                  onClick={() => vote(p.socketId)}
                  disabled={!!selected}
                  className={`w-full text-left px-3 py-2 rounded border ${selected === p.socketId ? 'bg-blue-500 text-white dark:bg-blue-600' : 'bg-white dark:bg-gray-800'} dark:text-gray-100`}
                >
                  {p.username || p.socketId}{selected === p.socketId ? ' (you voted)' : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === 'reveal' && lastResult && (
          <div className="mt-4">
            <div className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Question</div>
            <div className="mb-2 dark:text-gray-200">{gameState.results[gameState.results.length - 1].question}</div>
            <div className="font-semibold mb-2 text-gray-800 dark:text-gray-200">Votes</div>
            <div className="space-y-2">
              {lastResult.perPlayer.map((p) => (
                  <div key={p.socketId} className={`flex justify-between items-center p-2 border rounded ${lastResult.guessedCorrect && Array.isArray(lastResult.guessedCorrect) && lastResult.guessedCorrect.includes(p.socketId) ? 'bg-green-50 border-green-400 dark:bg-green-900' : 'bg-gray-50 dark:bg-gray-800'}`}>
                    <div className="font-medium text-gray-800 dark:text-gray-200">{p.username || p.socketId}{lastResult.guessedCorrect && Array.isArray(lastResult.guessedCorrect) && lastResult.guessedCorrect.includes(p.socketId) ? <span className="ml-2 inline-block text-sm text-green-800 dark:text-green-200">✓ guessed</span> : null}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {p.votesCount} vote{p.votesCount === 1 ? '' : 's'} {p.votes && p.votes.length > 0 ? `(${p.votes.map(v => gameState.players.find(pl => pl.socketId === v)?.username || v).join(', ')})` : ''}
                      {awardsRounded[p.socketId] ? ` — +${awardsRounded[p.socketId]} pts` : ''}
                    </div>
                  </div>
              ))}
            </div>
            <div className="mt-3 text-center text-gray-800 dark:text-gray-200">
              <div className="font-semibold">Majority winner: {majorityWinner ? (gameState.players.find(p => p.socketId === majorityWinner)?.username || majorityWinner) : 'No majority'}</div>
            </div>
                {/* Reveal timer (also shown during reveal) */}
                {['reveal'].includes(phase) && (
                  <div className="w-full max-w-md mx-auto mt-2 mb-3">
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 transition-none"
                        style={{ width: `${(1 - (timeLeft / (timeLimit || 10))) * 100}%`, transition: 'width 0.1s linear' }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeft}s left</div>
                  </div>
                )}
            {majorityWinner && votersForWinner.length > 0 && (
              <div className="mt-3 p-3 border rounded bg-green-50 dark:bg-green-900 text-center">
                <div className="font-semibold mb-1">Majority backers (voted for winner)</div>
                <div className="text-sm text-gray-800 dark:text-gray-200">{votersForWinner.map(sid => (gameState.players.find(p => p.socketId === sid)?.username || sid)).join(', ')}</div>
              </div>
            )}
          </div>
        )}

        {phase === 'guessing' && lastResult && lastResult.majorityWinner && (
          <MostLikelyGuessingSection
            lastResult={lastResult}
            gameState={gameState}
            user={user}
            submitAction={submitAction}
            onGameAction={onGameAction}
            timeLeft={timeLeft}
            timeLimit={timeLimit}
          />
        )}

        {phase === 'finished' && (
          <div className="mt-4">
            <div className="font-semibold mb-2">Final Scores</div>
            <div className="space-y-2">
              {Array.isArray(gameState && gameState.players ? gameState.players : []) && gameState.players.map(p => (
                <div key={p.socketId} className="flex justify-between items-center p-2 border rounded bg-gray-50 dark:bg-gray-800">
                  <div>{p.username || p.socketId}</div>
                  <div>{(gameState.scores && gameState.scores[p.socketId]) || 0} pts</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

MostLikelyGame.Guide = function MostLikelyGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Most Likely</h3>
      <p className="mb-1">Also known as the Voting Game. Players take turns asking questions, and everyone votes on who they think is most likely to fit the description.</p>
      <p className='mb-1'>Players can vote for themselves or others. After voting, the player with the most votes gets to guess who voted for them. Points are awarded based on votes received and correct guesses.</p>
    
      <p className="mb-1">The game is designed to be light-hearted and fun, encouraging players to think about their friends and who they believe fits the prompts best.</p>
    </div>
  );
};
